import queryString from "query-string";
import { useCallback } from "react";
import { NavigateOptions, useLocation, useNavigate } from "react-router";

// クエリパラメータのパース設定はレンダー間で不変なのでモジュールスコープに置く
const queryParseOption: queryString.ParseOptions = {
  arrayFormat: "bracket",
};

// NOTE: currentParams に存在するキーをリセットしたい場合は deleteKeys に指定する
//       params に指定してもキーは追加されるだけで、既存のキーは削除されないため
type PushHistoryOptions = NavigateOptions & { deleteKeys?: Array<string> };

// クエリパラメータを更新する関数を返すカスタムフック
// NOTE: WEB-2245 対応。pushHistory / buildQuery を useCallback 化して identity を安定させる。
//       これらを deps に含める呼び出し側 (updateStudentFilterByQuery など) の useCallback を
//       実効化するため。以前は毎レンダー新しい関数参照になり、依存する useCallback が
//       常に再生成される状態だった。
export const usePushHistory = <T>() => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const buildQuery = useCallback((params: T, deleteKeys?: Array<string>) => {
    const currentParams = queryString.parse(location.search, queryParseOption);
    if (deleteKeys) {
      deleteKeys.forEach((key) => delete currentParams[key]);
    }
    const newParams = {
      ...currentParams,
      ...params,
    };

    return queryString.stringify(newParams, queryParseOption);
  }, []);

  const pushHistory = useCallback(
    (params: T, options?: PushHistoryOptions) => {
      const { deleteKeys, ...navigateOptions } = options || {};
      const query = buildQuery(params, deleteKeys);
      navigate(`${pathname}?${query}`, navigateOptions);
    },
    [buildQuery, navigate, pathname],
  );

  return {
    pushHistory,
    buildQuery,
  };
};
